import { apiClient } from "@/lib/apiClient";
import type {
  EducationDetailDto,
  ExamAttemptDto,
  CertificateDto,
  EnrollmentDto,
} from "../types/client-education-detail.types";
import type { EducationFollowUpAction } from "../utils/follow-up-url";
import {
  expandScheduledTimes,
  type RawScheduledTime,
} from "../utils/scheduled-times";

/**
 * Backend scheduledTimes'ı haftalık tekrar `{ day }` olarak döndürür; kursiyer
 * tarafı somut tarihli `{ date }` bekler. Detay yanıtını okur okumaz hem
 * eğitim hem her sanal sınıf için haftalık girişleri kendi tarih aralıklarına
 * yayıp somut tarihlere çeviriyoruz — böylece resolve-cta / ActiveStageCard /
 * VirtualClassroomsSection / ClassSchedule / ics hiç değişmeden çalışır.
 */
function normalizeScheduledTimes(detail: EducationDetailDto): EducationDetailDto {
  if (!detail || typeof detail !== "object") return detail;

  const virtualClassrooms = detail.virtualClassrooms?.map((vc) => ({
    ...vc,
    scheduledTimes: expandScheduledTimes(
      vc.scheduledTimes as RawScheduledTime[] | null | undefined,
      vc.startDate,
      vc.endDate,
    ),
  }));

  return {
    ...detail,
    ...(virtualClassrooms ? { virtualClassrooms } : {}),
    scheduledTimes: expandScheduledTimes(
      detail.scheduledTimes as RawScheduledTime[] | null | undefined,
      detail.startDate,
      detail.endDate,
    ),
  };
}

/**
 * Backend envelope: { success, status, data: {...}, message }.
 * apiClient envelope'u otomatik açmıyor — burada elle çıkarıyoruz.
 * Aksi halde tüm alanlar (startDate, name, vb.) undefined gelir ve sayfa
 * yanlış erken-dönüş ekranlarına düşer.
 */
function unwrap<T>(payload: unknown): T {
  const env = payload as { data?: T } | T | null | undefined;
  if (env && typeof env === "object" && "data" in (env as object)) {
    const inner = (env as { data?: T }).data;
    if (inner !== undefined) return inner as T;
  }
  return payload as T;
}

export const clientEducationDetailApi = {
  getDetail: async (educationId: string): Promise<EducationDetailDto> => {
    const res = await apiClient.get<EducationDetailDto>(
      `/api/client/educations/${educationId}`
    );
    return normalizeScheduledTimes(unwrap<EducationDetailDto>(res.data));
  },

  getMyEnrollment: async (educationId: string): Promise<EnrollmentDto | null> => {
    try {
      const res = await apiClient.get<EnrollmentDto>(
        `/api/client/educations/${educationId}/enrollments/me`
      );
      return unwrap<EnrollmentDto | null>(res.data) ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  getMyExamAttempts: async (educationId: string): Promise<ExamAttemptDto[]> => {
    try {
      const res = await apiClient.get<ExamAttemptDto[] | { items?: ExamAttemptDto[] }>(
        `/api/client/educations/${educationId}/exam/me`
      );
      const data = unwrap<ExamAttemptDto[] | { items?: ExamAttemptDto[] }>(res.data);
      if (Array.isArray(data)) return data;
      return data?.items ?? [];
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return [];
      throw err;
    }
  },

  getMyCertificate: async (
    educationId: string,
    enrollmentId: string
  ): Promise<CertificateDto | null> => {
    try {
      const res = await apiClient.get<CertificateDto>(
        `/api/client/educations/${educationId}/enrollments/${enrollmentId}/certificate`
      );
      return unwrap<CertificateDto | null>(res.data) ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  completeObjective: async (
    enrollmentId: string,
    objectiveId: string,
    body?: { watchedSeconds?: number; lastPositionSeconds?: number; progressPercent?: number }
  ): Promise<EducationFollowUpAction | null> => {
    const { data } = await apiClient.post<{
      data?: EducationFollowUpAction | null;
      followUpAction?: EducationFollowUpAction | null;
    } | EducationFollowUpAction | null>(
      `/api/client/education-enrollments/${enrollmentId}/objectives/${objectiveId}/complete`,
      body ?? {}
    );
    // Olası response şekilleri:
    //   - null                                            → henüz tamamlanmadı veya sertifika üretiliyor
    //   - { displayType, type, ... }                     → doğrudan action objesi
    //   - { data: { ...action } }                         → envelope içinde
    //   - { followUpAction: { ...action } }               → named field
    if (data == null) return null;
    if (typeof data === "object" && "displayType" in data) {
      return data as EducationFollowUpAction;
    }
    const envelope = data as {
      data?: EducationFollowUpAction | null;
      followUpAction?: EducationFollowUpAction | null;
    };
    return envelope.data ?? envelope.followUpAction ?? null;
  },

  updateObjectiveProgress: async (
    _enrollmentId: string,
    objectiveId: string,
    body: { watchedSeconds: number; lastPositionSeconds: number; progressPercent: number; watchedSecondsDelta?: number }
  ): Promise<{
    watchedSeconds?: number;
    lastPositionSeconds?: number;
    maxWatchedPosition?: number;
    unlockedUntilSeconds?: number | null;
    progressPercent?: number;
    isCompleted?: boolean;
  }> => {
    const { data } = await apiClient.post<{
      data?: {
        watchedSeconds?: number;
        lastPositionSeconds?: number;
        maxWatchedPosition?: number;
        unlockedUntilSeconds?: number | null;
        progressPercent?: number;
        isCompleted?: boolean;
      };
      watchedSeconds?: number;
      lastPositionSeconds?: number;
      maxWatchedPosition?: number;
      unlockedUntilSeconds?: number | null;
      progressPercent?: number;
      isCompleted?: boolean;
    }>(
      `/api/client/objectives/${objectiveId}/video/watch`,
      {
        watchedSecondsDelta: body.watchedSecondsDelta ?? body.watchedSeconds,
        lastPositionSeconds: body.lastPositionSeconds,
      }
    );
    return data.data ?? data;
  },

  getObjectiveVideoWatch: async (
    objectiveId: string,
  ): Promise<{
    watchedSeconds?: number;
    lastPositionSeconds?: number;
    maxWatchedPosition?: number;
    unlockedUntilSeconds?: number | null;
    progressPercent?: number;
    isCompleted?: boolean;
  }> => {
    const { data } = await apiClient.get<{
      data?: {
        watchedSeconds?: number;
        lastPositionSeconds?: number;
        maxWatchedPosition?: number;
        unlockedUntilSeconds?: number | null;
        progressPercent?: number;
        isCompleted?: boolean;
      };
      watchedSeconds?: number;
      lastPositionSeconds?: number;
      maxWatchedPosition?: number;
      unlockedUntilSeconds?: number | null;
      progressPercent?: number;
      isCompleted?: boolean;
    }>(`/api/client/objectives/${objectiveId}/video/watch`);
    return data.data ?? data;
  },

  answerObjectiveVideoQuestion: async (
    objectiveId: string,
    body: { questionID: string; selectedOptionID: string },
  ): Promise<{
    isCorrect: boolean;
    unlockedUntilSeconds?: number | null;
  }> => {
    const { data } = await apiClient.post<{
      data?: {
        isCorrect: boolean;
        unlockedUntilSeconds?: number | null;
      };
      isCorrect: boolean;
      unlockedUntilSeconds?: number | null;
    }>(
      `/api/client/objectives/${objectiveId}/video/questions/answer`,
      body,
      { skipErrorToast: true },
    );
    return data.data ?? data;
  },

  cancelEnrollment: async (enrollmentId: string): Promise<void> => {
    await apiClient.post(
      `/api/client/education-enrollments/${enrollmentId}/cancel`
    );
  },

  /**
   * Sınıf (virtual classroom) katılımcı listesini döner.
   * Kursiyer kendi sınıfındaki diğer kişileri görebilsin diye kullanılır.
   * Backend: GET /virtual-classrooms/:id/participants — EDUCATIONS.VIEW yetkisi gerekir.
   */
  getClassroomParticipants: async (
    classroomId: string,
  ): Promise<
    Array<{
      id: string;
      userID: string;
      firstName?: string;
      lastName?: string;
      title?: string | null;
      department?: { id: string; name: string } | null;
    }>
  > => {
    try {
      const res = await apiClient.get(
        `/api/client/virtual-classrooms/${classroomId}/participants`,
      );
      const data = unwrap<{ items?: Array<unknown> } | Array<unknown>>(res.data);
      const list = Array.isArray(data) ? data : (data?.items ?? []);
      return (list as Array<Record<string, unknown>>).map((p) => {
        const u = (p.user ?? p) as Record<string, unknown>;
        return {
          id: String(p.id ?? u.id ?? ""),
          userID: String(p.userID ?? u.id ?? ""),
          firstName: (u.firstName as string) ?? undefined,
          lastName: (u.lastName as string) ?? undefined,
          title: (u.title as string) ?? null,
          department:
            (u.department as { id: string; name: string } | null) ?? null,
        };
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404 || status === 403) return [];
      throw err;
    }
  },
};
